/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
// FormContent.js
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  FormControl,
  Typography,
  makeStyles,
  Divider,
  Grid,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  Button,
  Fab,
  TextField,
} from "@material-ui/core";

import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useSelector } from "react-redux";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import ControlRenderer from "./ControlRenderer";
import { USER_TYPE } from "../../constants";
import DividendInformation from "../IEPFReport/DividendInformation";

const useStyles = makeStyles((theme) => ({
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f9f9f9",
    boxShadow:
      "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px",
  },
  mainSectionTitle: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    fontWeight: 600,
    color: "#04286F",
  },
  subSectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "1.1rem",
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  questionGrid: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(0),
  },
  questionGridItem: {
    display: "flex",
    alignContent: "flex-end",
    flexWrap: "wrap",
  },
  questionLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: "rgba(3, 64, 120, 1)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  required: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5),
  },
  sectionDivider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  subSectionPaper: {
    padding: theme.spacing(2, 0),
    backgroundColor: "#f9f9f9",
    boxShadow: "none",
  },
  confidenceBadge: {
    fontSize: "0.5rem",
    height: "16px",
    marginLeft: theme.spacing(0.4),
  },
  confidenceHigh: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  confidenceMedium: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  confidenceLow: {
    backgroundColor: "#f44336",
    color: "white",
  },
  aiReasonIcon: {
    padding: 2,
    color: "#555", // or your theme color
    "&:hover": {
      color: "#000",
    },
  },
  // Masking styles
  maskingButton: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1000,
    background: "linear-gradient(90deg, #0A2D71 0%, #133984 100%)",
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  maskingButtonSecondary: {
    background: "linear-gradient(90deg, #A21717 0%, #A91067 100%);",

    "&:hover": {
      background: "linear-gradient(90deg, #A21717 0%, #A91067 100%)",
    },
  },
  formHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: theme.spacing(1),
  },
  maskingControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  maskingStatus: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const roles = [USER_TYPE.APPROVER];

const FormContent = ({
  currentFormData,
  formResponses,
  loading,
  error,
  onControlChange,
  onMultiControlChange,
  validationErrors,
  onFormRemarkChange,
  formRemark,
  formDividendEntries,
  ...props
}) => {
  const classes = useStyles();
  const [isMasked, setIsMasked] = useState(true);
  const { isAuditor } = useSelector((state) => state.auth.user);
  const [remarkError, setRemarkError] = useState("");

  const disallowedRemarkPattern = /[@#!?<>$%^*{}|~`=]/;

  // Toggle masking for all fields
  const toggleMasking = () => {
    setIsMasked(!isMasked);
  };

  // Function to get badge color class based on confidence level
  const getConfidenceBadgeClass = (confidence) => {
    switch (confidence?.toUpperCase()) {
      case "HIGH":
        return classes.confidenceHigh;
      case "MEDIUM":
        return classes.confidenceMedium;
      case "LOW":
        return classes.confidenceLow;
      default:
        return "";
    }
  };

  // Function to render confidence badge
  const renderConfidenceBadge = (question) => {
    if (!question.confidence) {
      return null;
    }

    return (
      <Chip
        label={question.confidence.toUpperCase()}
        size="small"
        className={`${classes.confidenceBadge} ${getConfidenceBadgeClass(
          question.confidence
        )}`}
      />
    );
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!currentFormData || !currentFormData.sections) {
    return (
      <Box p={3}>
        <Typography>No form data available.</Typography>
      </Box>
    );
  }

  // Group sections by section_name
  const groupedSections = currentFormData.sections.reduce((acc, section) => {
    const sectionName = section.section_name;
    if (!acc[sectionName]) {
      acc[sectionName] = [];
    }
    acc[sectionName].push(section);
    return acc;
  }, {});

  // Sort each group by sequence
  Object.keys(groupedSections).forEach((sectionName) => {
    groupedSections[sectionName].sort((a, b) => a.sequence - b.sequence);
  });

  // Get unique section names in order of appearance
  const sectionOrder = [
    ...new Set(
      currentFormData.sections
        .sort((a, b) => a.sequence - b.sequence)
        .map((section) => section.section_name)
    ),
  ];

  return (
    <>
      {/* Global Masking Controls */}
      {/* <div className={classes.formHeader}>
        <Typography variant="h6" color="primary">
          Form Content
        </Typography>
        <div className={classes.maskingControls}>
          <Typography className={classes.maskingStatus}>
            {isMasked ? "Content Hidden" : "Content Visible"}
          </Typography>
          <Button
            variant="contained"
            color={isMasked ? "secondary" : "primary"}
            startIcon={isMasked ? <Visibility /> : <VisibilityOff />}
            onClick={toggleMasking}
            size="small"
          >
            {isMasked ? "Show All" : "Hide All"}
          </Button>
        </div>
      </div> */}

      {/* Floating Action Button for quick access */}
      <Tooltip title={isMasked ? "Show all fields" : "Hide all fields"} arrow>
        <Fab
          className={`${classes.maskingButton} ${
            isMasked ? classes.maskingButtonSecondary : ""
          }`}
          onClick={toggleMasking}
          size="medium"
        >
          {isMasked ? <Visibility /> : <VisibilityOff />}
        </Fab>
      </Tooltip>

      {/* Form Sections */}
      {sectionOrder.map((sectionName, sectionIndex) => (
        <div key={sectionName}>
          {/* Main Section Title */}
          <Typography variant="h5" className={classes.mainSectionTitle}>
            {sectionName}
          </Typography>

          {sectionIndex > 0 && <Divider className={classes.sectionDivider} />}

          {/* Subsections within this main section */}
          {groupedSections[sectionName].map((subsection, index) => (
            <Paper
              key={subsection.id}
              className={classes.subSectionPaper}
              elevation={1}
            >
              {/* Subsection Title (Header Name) */}
              <Typography variant="h6" className={classes.subSectionTitle}>
                {`${index + 1}.`} {subsection.header_name}
              </Typography>

              {/* Questions Grid for this subsection */}
              <Grid container spacing={2} className={classes.questionGrid}>
                {subsection.questions &&
                  subsection.questions
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((question) => {
                      const isInvalid = !!validationErrors[question.id];

                      return (
                        <Grid
                          item
                          xs={12}
                          md={6}
                          lg={4}
                          key={question.id}
                          className={classes.questionGridItem}
                        >
                          <FormControl
                            className={classes.formControl}
                            required={question.is_required}
                            error={isInvalid}
                          >
                            <div className={classes.questionLabel}>
                              <span>
                                {question.question_text}

                                {question.is_required && (
                                  <span className={classes.required}>*</span>
                                )}
                                {question.ai_answer_reason && (
                                  <Tooltip
                                    title={question.ai_answer_reason}
                                    arrow
                                  >
                                    <IconButton
                                      size="small"
                                      className={classes.aiReasonIcon}
                                      style={{ marginLeft: 4 }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {renderConfidenceBadge(question)}
                              </span>
                            </div>

                            <ControlRenderer
                              question={question}
                              value={
                                formResponses[question.id] || question.value
                              }
                              onChange={
                                question.control_type === "checkbox"
                                  ? onMultiControlChange
                                  : onControlChange
                              }
                              disabled={isAuditor}
                              isMasked={isMasked}
                              isInvalid={isInvalid} // 4. Pass the flag down to the renderer
                            />
                          </FormControl>
                        </Grid>
                      );
                    })}
              </Grid>
            </Paper>
          ))}
        </div>
      ))}

      <Divider className={classes.sectionDivider} />

      {formDividendEntries && (
        <DividendInformation
          dividendEntries={formDividendEntries}
          disabled
          styles={{
            paper: { margin: 0, boxShadow: "none", padding: 0 },
            header: { fontWeight: 600, textTransform: "uppercase" },
          }}
        />
      )}

      <Divider className={classes.sectionDivider} />

      <div style={{ marginTop: "24px" }}>
        <Typography variant="h6" className={classes.mainSectionTitle}>
          REMARK
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl className={classes.formControl}>
              <TextField
                id="remark"
                name="remark"
                variant="outlined"
                value={formRemark || ""}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (disallowedRemarkPattern.test(newValue)) {
                    setRemarkError(
                      "Special characters like @, #, !, ?, <, >, $, %, ^, *, {, }, |, ~, `, and = not allowed"
                    );
                  } else {
                    const cleanValue = newValue.replace(/\s+/g, " ");
                    setRemarkError("");
                    onFormRemarkChange(cleanValue);
                  }
                }}
                fullWidth
                multiline
                rows={4}
                disabled={isAuditor}
                error={!!remarkError}
                helperText={remarkError}
              />
            </FormControl>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

FormContent.propTypes = {
  currentFormData: PropTypes.object,
  formResponses: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onControlChange: PropTypes.func.isRequired,
  onMultiControlChange: PropTypes.func.isRequired,
  formRemark: PropTypes.string,
  onFormRemarkChange: PropTypes.func.isRequired,
};

export default FormContent;
